


import React, { useState, useEffect, useCallback } from "react";
import APIConfig from "../../Common/Configurations/APIConfig";
import Navbar from "../../Navigation/Navbar";
import { Search, ChevronLeft, ChevronRight, Map, FileText, Download, RotateCcw, Eye } from "lucide-react";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; 
import * as XLSX from "xlsx";

const MemberReport = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const [tempFilters, setTempFilters] = useState({
    searchTerm: "", fromDate: "", toDate: "", status: "All", paymentStatus: "All"
  });

  const [appliedFilters, setAppliedFilters] = useState({
    searchTerm: "", fromDate: "", toDate: "", status: "All", paymentStatus: "All"
  });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "N/A" : `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  const fetchMembers = useCallback(async (isExport = false) => {
    if (!isExport) setLoading(true);
    try {
      const pSize = isExport ? 5000 : pageSize; 
      const pNum = isExport ? 1 : currentPage;

      const params = new URLSearchParams({
        pageNumber: pNum,
        pageSize: pSize,
        searchTerm: appliedFilters.searchTerm,
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
        status: appliedFilters.status,
        paymentStatus: appliedFilters.paymentStatus === "Fully Paid" ? "Paid" : appliedFilters.paymentStatus === "Pending" ? "Pending" : "All"
      });

      const res = await fetch(`${APIConfig.APIACTIVEURL}${APIConfig.MEMBER_GET_ALL}?${params.toString()}`);
      const result = await res.json();
      
      if (isExport) return result.data || [];
      
      setMembers(result.data || []);
      setTotalPages(result.totalPages || 1);
    } catch (e) {
      console.error(e);
      return [];
    } finally {
      if (!isExport) setLoading(false);
    }
  }, [currentPage, appliedFilters]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleSearchClick = () => {
    setCurrentPage(1);
    setAppliedFilters({ ...tempFilters });
  };

  const handleClear = () => {
    const reset = { searchTerm: "", fromDate: "", toDate: "", status: "All", paymentStatus: "All" };
    setTempFilters(reset);
    setAppliedFilters(reset);
    setCurrentPage(1);
  };

  const handleExportExcel = async () => {
    Swal.fire({ title: 'Generating Excel...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    const data = await fetchMembers(true);
    const sheetData = data.map(m => ({
      "Name": m.memberName, "Phone": m.phoneNo, "Joined": formatDate(m.joiningDate),
      "Plots": m.memberPlots?.map(p => p.plot?.plotNumber).join(", "),
      "Total": m.balanceAmount || 0, "Paid": m.paidAmount || 0, "Bal": (m.balanceAmount || 0) - (m.paidAmount || 0), "Status": m.status
    }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `Report_${Date.now()}.xlsx`);
    Swal.close();
  };

  const handleExportPDF = async () => {
    Swal.fire({ title: 'Generating PDF...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    const data = await fetchMembers(true);
    const doc = new jsPDF('landscape');
    const rows = data.map(m => [m.memberName, m.phoneNo, formatDate(m.joiningDate), m.memberPlots?.map(p => p.plot?.plotNumber).join(", "), (m.balanceAmount || 0).toLocaleString(), (m.paidAmount || 0).toLocaleString(), ((m.balanceAmount || 0) - (m.paidAmount || 0)).toLocaleString(), m.status]);
    autoTable(doc, { head: [['Name', 'Phone', 'Joined', 'Plots', 'Total', 'Paid', 'Balance', 'Status']], body: rows, startY: 20, theme: 'grid', headStyles: { fillColor: [79, 70, 229] } });
    doc.save(`Report_${Date.now()}.pdf`);
    Swal.close();
  };

  const styles = {
    container: { padding: "40px", backgroundColor: "#f4f7fe", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
    filterRow: { 
      display: "grid", 
      gridTemplateColumns: "1.5fr 1fr 1fr 1fr 1fr auto auto", 
      gap: "20px", 
      marginBottom: "30px", 
      alignItems: "end", 
      backgroundColor:'#fff', 
      padding:'30px', 
      borderRadius:'20px', 
      boxShadow:'0 10px 30px rgba(0,0,0,0.05)' 
    },
    label: { fontSize: "12px", fontWeight: "800", color: "#1b2559", textTransform: "uppercase", marginBottom: '10px', display:'block' },
    input: { padding: "12px 15px", borderRadius: "12px", border: "1.5px solid #e2e8f0", fontSize: "14px", outline: "none", width: '100%', boxSizing: 'border-box', color:'#1b2559' },
    searchBtn: { backgroundColor: "#4f46e5", color: "#fff", border: "none", padding: "14px 24px", borderRadius: "12px", cursor: "pointer", fontWeight: "700", display: 'flex', alignItems: 'center', justifyContent: 'center' },
    card: { backgroundColor: "#fff", borderRadius: "24px", boxShadow: "0 10px 40px rgba(0,0,0,0.04)", overflow: "hidden" },
    badge: (isPaid) => ({ padding: "6px 12px", borderRadius: "8px", fontSize: "11px", fontWeight: "800", backgroundColor: isPaid ? "#dcfce7" : "#fef2f2", color: isPaid ? "#059669" : "#ef4444", display:'inline-block', marginTop:'5px' })
  };

  return (
    <>
      <Navbar />
      <div style={styles.container}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "40px" }}>
          <div>
            <h2 style={{ fontSize: "36px", fontWeight: "900", color: "#1b2559", margin: 0 }}>Members Report </h2>
          
          </div>
          <div style={{ display: 'flex', gap: '15px' }}>
            <button onClick={handleExportExcel} style={{ ...styles.searchBtn, backgroundColor: '#059669' }}><Download size={20} style={{marginRight:'10px'}}/> Download Excel</button>
            <button onClick={handleExportPDF} style={{ ...styles.searchBtn, backgroundColor: '#e11d48' }}><FileText size={20} style={{marginRight:'10px'}}/> Download PDF</button>
          </div>
        </div>

        <div style={styles.filterRow}>
          <div><label style={styles.label}>Search Member</label>
            <input style={styles.input} placeholder="Name, Phone, Plot..." value={tempFilters.searchTerm} onChange={(e) => setTempFilters({...tempFilters, searchTerm: e.target.value})} />
          </div>
          <div><label style={styles.label}>From Date</label>
            <input type="date" style={styles.input} value={tempFilters.fromDate} onChange={(e) => setTempFilters({...tempFilters, fromDate: e.target.value})} />
          </div>
          <div><label style={styles.label}>To Date</label>
            <input type="date" style={styles.input} value={tempFilters.toDate} onChange={(e) => setTempFilters({...tempFilters, toDate: e.target.value})} />
          </div>
          <div><label style={styles.label}>Status</label>
            <select style={styles.input} value={tempFilters.status} onChange={(e) => setTempFilters({...tempFilters, status: e.target.value})}>
                <option value="All">All Status</option><option value="ACTIVE">Active</option><option value="INACTIVE">Inactive</option>
            </select>
          </div>
          <div><label style={styles.label}>Payment</label>
            <select style={styles.input} value={tempFilters.paymentStatus} onChange={(e) => setTempFilters({...tempFilters, paymentStatus: e.target.value})}>
                <option value="All">All</option><option value="Fully Paid">Fully Paid</option><option value="Pending">Pending</option>
            </select>
          </div>
          <button style={styles.searchBtn} onClick={handleSearchClick} title="Search"><Search size={22}/></button>
          <button style={{...styles.searchBtn, backgroundColor:'#f4f7fe', color:'#a3aed0'}} onClick={handleClear} title="Reset Filters"><RotateCcw size={22}/></button>
        </div>

        <div style={styles.card}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f8fafc", color: "#a3aed0", fontSize: "12px", fontWeight: "800", textTransform: "uppercase", borderBottom:'1px solid #f1f5f9' }}>
                <th style={{ padding: "25px 20px", textAlign: "left" }}>Member Info</th>
                <th style={{ padding: "25px 20px", textAlign: "left" }}>Joined Date</th>
                <th style={{ padding: "25px 20px", textAlign: "left" }}>Plots</th>
                <th style={{ padding: "25px 20px", textAlign: "left" }}>Financial Summary</th>
                <th style={{ padding: "25px 20px", textAlign: "center" }}>View</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (<tr><td colSpan="5" style={{ padding: "80px", textAlign: "center", color: "#a3aed0", fontSize: "18px", fontWeight: "700" }}>Analyzing Data...</td></tr>) : 
              members.length === 0 ? (<tr><td colSpan="5" style={{ padding: "80px", textAlign: "center", color: "#a3aed0", fontSize: "16px" }}>No matching records found.</td></tr>) :
              members.map((m) => {
                const total = m.balanceAmount || 0;
                const paid = m.paidAmount || 0;
                const bal = total - paid;
                return (
                  <tr key={m.memberId} style={{ borderBottom: "1px solid #f4f7fe" }}>
                    <td style={{ padding: "25px 20px" }}>
                      <div style={{ fontWeight: "900", color: "#1b2559", fontSize:'16px' }}>{m.memberName}</div>
                      <div style={{ fontSize: "13px", color: "#a3aed0", fontWeight:'600', marginTop:'4px' }}>{m.phoneNo}</div>
                    </td>
                    <td style={{ padding: "25px 20px", color: '#475569', fontWeight:'700' }}>{formatDate(m.joiningDate)}</td>
                    <td style={{ padding: "25px 20px" }}>
                      {m.memberPlots?.map((mp, i) => <div key={i} style={{ fontSize: "13px", fontWeight: "800", color:'#4f46e5' }}>• {mp.plot?.plotNumber}</div>)}
                    </td>
                    <td style={{ padding: "25px 20px" }}>
                      <div style={{ fontSize: "13px", color: "#64748b", fontWeight:'700' }}>Total: ₹{total.toLocaleString()}</div>
                      <div style={{ fontSize: "13px", color: "#64748b", fontWeight:'700' }}>Paid: ₹{paid.toLocaleString()}</div>
                      <div style={styles.badge(bal <= 0)}>Bal: ₹{bal.toLocaleString()}</div>
                    </td>
                    <td style={{ padding: "25px 20px", textAlign: 'center' }}>
                      <button onClick={() => window.open(`/members/view/${m.memberId}`, "_blank")} style={{border:'none', background:'#eff6ff', color:'#3b82f6', padding:'12px', borderRadius:'14px', cursor:'pointer'}}><Eye size={22} /></button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {/* PAGINATION RESTORED */}
          <div style={{ padding: "30px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1.5px solid #f1f5f9" }}>
            <span style={{ fontSize: "15px", color: "#1b2559", fontWeight: '800' }}>Page <span style={{color:'#4f46e5'}}>{currentPage}</span> of {totalPages}</span>
            <div style={{ display: "flex", gap: "15px" }}>
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(c => c - 1)} style={{...styles.searchBtn, padding:'10px 20px', backgroundColor: currentPage === 1 ? '#f1f5f9' : '#fff', color: '#1b2559', border: '1.5px solid #e2e8f0'}}><ChevronLeft size={20}/> Prev</button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(c => c + 1)} style={{...styles.searchBtn, padding:'10px 20px', backgroundColor: currentPage === totalPages ? '#f1f5f9' : '#fff', color: '#1b2559', border: '1.5px solid #e2e8f0'}}>Next <ChevronRight size={20}/></button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MemberReport;